import { fetchWithAuth } from "./FetchWithAuth";
import { url } from "./url";

const BASE = `${String(url).replace(/\/+$/, "")}`;

/**
 * Single aggregated CRM dashboard payload (replaces 11 separate calls).
 * Requires start_date and end_date (YYYY-MM-DD). Optional counsellor_id.
 */
export async function getCrmDashboardSummary(params = {}, signal = null) {
  const { start_date, end_date, counsellor_id } = params;
  if (!start_date || !end_date) {
    throw new Error("start_date and end_date are required");
  }
  const sp = new URLSearchParams({ start_date, end_date });
  if (counsellor_id != null) sp.set("counsellor_id", String(counsellor_id));
  const response = await fetchWithAuth(`${BASE}/api/dashboard/crm-summary?${sp.toString()}`, {
    method: "GET",
    credentials: "include",
    signal,
    retry: true,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Dashboard request failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Counsellor dashboard: single aggregated payload (overview, incentives, performance, recent, trends, targets, follow_ups, tasks, insights, alerts).
 * Cached per counsellor + date range (TTL 60s).
 * @param {{ start_date?: string, end_date?: string }} params - optional date range (YYYY-MM-DD)
 */
export async function getCounsellorDashboard(params = {}, signal = null) {
  const sp = new URLSearchParams();
  if (params?.start_date) sp.set("start_date", params.start_date);
  if (params?.end_date) sp.set("end_date", params.end_date);
  if (params?.include_incentives === false) sp.set("include_incentives", "0");
  const qs = sp.toString();
  const path = `${BASE}/dashboard/counsellor${qs ? `?${qs}` : ""}`;
  const response = await fetchWithAuth(path, {
    method: "GET",
    credentials: "include",
    signal,
    retry: true,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Counsellor dashboard failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Back Office dashboard: overview (active/finished classes, enquiries without allocation), calendar, recent/upcoming classes.
 * Optional start_date, end_date (YYYY-MM-DD) for calendar range.
 */
export async function getBackOfficeDashboard(params = {}, signal = null) {
  const sp = new URLSearchParams();
  if (params?.start_date) sp.set("start_date", params.start_date);
  if (params?.end_date) sp.set("end_date", params.end_date);
  const qs = sp.toString();
  const response = await fetchWithAuth(`${BASE}/api/dashboard/back-office${qs ? `?${qs}` : ""}`, {
    method: "GET",
    credentials: "include",
    signal,
    retry: true,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Back Office dashboard failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Accounts dashboard: collections, payment tracking (overview, outstanding, pending, overdue, transactions, etc.).
 * Cached TTL 60s.
 */
export async function getAccountsDashboard(signal = null) {
  const response = await fetchWithAuth(`${BASE}/dashboard/accounts`, {
    method: "GET",
    credentials: "include",
    signal,
    retry: true,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Accounts dashboard failed: ${response.status}`);
  }
  return response.json();
}
